// LabCatalogPage - Lab catalog within a workspace
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import api, { categoryAPI } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  FlaskConical, Search, Terminal, Clock, CheckCircle,
  PlayCircle, Loader2, ArrowRight, BookOpen, Layers
} from "lucide-react";

const CATEGORIES = {
  docker: { label: "Docker", color: "bg-blue-500/20 text-blue-400", icon: "🐳" },
  kubernetes: { label: "Kubernetes", color: "bg-purple-500/20 text-purple-400", icon: "☸️" },
  linux: { label: "Linux", color: "bg-orange-500/20 text-orange-400", icon: "🐧" },
  ansible: { label: "Ansible", color: "bg-red-500/20 text-red-400", icon: "🅰️" },
  terraform: { label: "Terraform", color: "bg-indigo-500/20 text-indigo-400", icon: "🏗️" },
  devops: { label: "DevOps", color: "bg-cyan-500/20 text-cyan-400", icon: "♾️" },
  cloud: { label: "Cloud", color: "bg-emerald-500/20 text-emerald-400", icon: "☁️" },
  networking: { label: "Réseau", color: "bg-amber-500/20 text-amber-400", icon: "🌐" },
  security: { label: "Sécurité", color: "bg-rose-500/20 text-rose-400", icon: "🔒" },
  other: { label: "Autre", color: "bg-gray-500/20 text-gray-400", icon: "📦" },
};

const DIFFICULTIES = {
  beginner: { label: "Débutant", color: "bg-green-500/20 text-green-400" },
  intermediate: { label: "Intermédiaire", color: "bg-yellow-500/20 text-yellow-400" },
  advanced: { label: "Avancé", color: "bg-red-500/20 text-red-400" },
};

export default function LabCatalogPage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [labs, setLabs] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [wsCategories, setWsCategories] = useState([]);
  const [activeWsCat, setActiveWsCat] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [labsRes, sessionsRes] = await Promise.all([
          api.get(`/labs/workspace/${workspaceId}`),
          api.get(`/labs/sessions/user/${user?.id}?workspace_id=${workspaceId}`),
        ]);
        setLabs(labsRes.data);
        setSessions(sessionsRes.data);
      } catch (err) {
        console.error("Failed to load labs:", err);
      } finally {
        setIsLoading(false);
      }
    };
    if (user?.id) fetchData();
    categoryAPI.list(workspaceId).then(res => setWsCategories(res.data || [])).catch(() => {});
  }, [workspaceId, user?.id]);

  const getLabStatus = (labId) => {
    const session = sessions.find(s => s.lab_id === labId);
    if (!session) return { status: "not_started", label: "Pas commencé", session: null };
    if (session.status === "completed") return { status: "completed", label: "Terminé", session };
    return { status: "in_progress", label: "En cours", session };
  };

  // Workspace category grouping
  const hasWsCategories = wsCategories.length > 0;
  const labsByWsCat = labs.reduce((acc, lab) => {
    const key = lab.category_id || "__uncategorized__";
    if (!acc[key]) acc[key] = [];
    acc[key].push(lab);
    return acc;
  }, {});
  const wsCatGroups = (() => {
    if (!hasWsCategories) return [];
    const result = [];
    wsCategories.forEach(cat => {
      if (labsByWsCat[cat.id]) result.push({ key: cat.id, label: cat.name, color: cat.color, items: labsByWsCat[cat.id] });
    });
    if (labsByWsCat["__uncategorized__"]) result.push({ key: "__uncategorized__", label: "Sans catégorie", color: null, items: labsByWsCat["__uncategorized__"] });
    return result;
  })();

  // Auto-select first workspace category tab
  useEffect(() => {
    if (!hasWsCategories || wsCatGroups.length === 0) return;
    if (!wsCatGroups.some(g => g.key === activeWsCat)) {
      setActiveWsCat(wsCatGroups[0].key);
    }
  }, [labs, wsCategories]);

  // Filter: workspace category first, then search + hardcoded category
  const wsCatFiltered = hasWsCategories
    ? (wsCatGroups.find(g => g.key === activeWsCat)?.items || [])
    : labs;

  const filteredLabs = wsCatFiltered.filter(lab => {
    const matchSearch = lab.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                        lab.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchCategory = categoryFilter === "all" || lab.category === categoryFilter;
    return matchSearch && matchCategory;
  });

  const startLab = (labId) => {
    navigate(`/workspace/${workspaceId}/labs/${labId}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center space-y-4">
          <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground">Chargement des labs...</p>
        </div>
      </div>
    );
  }

  return (
    <WorkspaceLayout>
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="p-3 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl border border-cyan-500/30">
          <FlaskConical className="h-8 w-8 text-cyan-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Labs Interactifs</h1>
          <p className="text-muted-foreground">Pratiquez avec des exercices interactifs et un terminal intégré</p>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="flex items-center gap-6 p-4 bg-gradient-to-r from-cyan-500/5 via-blue-500/5 to-purple-500/5 rounded-xl border border-border">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-cyan-400" />
          <span className="text-sm font-medium">{labs.length} labs</span>
        </div>
        <div className="w-px h-4 bg-border" />
        <div className="flex items-center gap-2">
          <PlayCircle className="h-4 w-4 text-yellow-400" />
          <span className="text-sm font-medium">{sessions.filter(s => s.status === "in_progress").length} en cours</span>
        </div>
        <div className="w-px h-4 bg-border" />
        <div className="flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-green-400" />
          <span className="text-sm font-medium">{sessions.filter(s => s.status === "completed").length} terminés</span>
        </div>
      </div>

      {/* Search + Category Tabs */}
      <div className="space-y-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Rechercher un lab..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" />
        </div>
        {/* Workspace Category Tabs */}
        {hasWsCategories && wsCatGroups.length > 0 && (
          <div className="flex gap-2 flex-wrap pb-2 border-b border-border overflow-x-auto">
            {wsCatGroups.map(group => (
              <button
                key={group.key}
                onClick={() => setActiveWsCat(group.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                  activeWsCat === group.key
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                {group.color && (
                  <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: group.color }} />
                )}
                {group.label}
                <span className="ml-1 bg-background/50 text-xs px-1.5 py-0.5 rounded-full">{group.items.length}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Labs Grid */}
      {filteredLabs.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-16 text-center">
            <FlaskConical className="h-16 w-16 mx-auto text-muted-foreground/20 mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground mb-2">Aucun lab disponible</h3>
            <p className="text-sm text-muted-foreground/60">Les labs seront ajoutés prochainement par votre formateur</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredLabs.map(lab => {
            const { status, label, session } = getLabStatus(lab.id);
            const cat = CATEGORIES[lab.category] || CATEGORIES.other;
            const diff = DIFFICULTIES[lab.difficulty] || DIFFICULTIES.beginner;
            const progress = session ? Math.round((session.completed_steps?.length || 0) / (lab.steps_count || 1) * 100) : 0;

            return (
              <Card key={lab.id} className="group hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all cursor-pointer overflow-hidden" onClick={() => startLab(lab.id)}>
                {/* Category color bar */}
                <div className={`h-1 ${cat.color.replace('text-', 'bg-').replace('/20', '/40')}`} />

                <CardContent className="p-5 space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">{cat.icon}</span>
                      <Badge className={cat.color}>{cat.label}</Badge>
                    </div>
                    {status === "completed" && (
                      <Badge className="bg-green-500/20 text-green-400">
                        <CheckCircle className="h-3 w-3 mr-1" /> Terminé
                      </Badge>
                    )}
                    {status === "in_progress" && (
                      <Badge className="bg-yellow-500/20 text-yellow-400">
                        <PlayCircle className="h-3 w-3 mr-1" /> En cours
                      </Badge>
                    )}
                  </div>

                  <div>
                    <h3 className="font-semibold text-base group-hover:text-primary transition-colors line-clamp-2">
                      {lab.title}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                      {lab.description}
                    </p>
                  </div>

                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <Badge className={diff.color}>{diff.label}</Badge>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {lab.duration_minutes} min
                    </span>
                    <span className="flex items-center gap-1">
                      <BookOpen className="h-3 w-3" /> {lab.steps_count} étapes
                    </span>
                  </div>

                  {/* Progress bar for in-progress labs */}
                  {status === "in_progress" && (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Progression</span>
                        <span className="font-mono text-primary">{progress}%</span>
                      </div>
                      <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
                      </div>
                    </div>
                  )}

                  <Button variant={status === "not_started" ? "default" : "outline"} className="w-full gap-2 group-hover:bg-primary group-hover:text-primary-foreground transition-colors" size="sm">
                    {status === "completed" ? "Voir les résultats" : status === "in_progress" ? "Continuer" : "Commencer"}
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
    </WorkspaceLayout>
  );
}
